import { ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';

import type {
  CostBreakdown,
  DebugPayloadSkeleton,
  PayloadSection,
} from '@/features/votes/message-votes';
import { cn } from '@/lib/utils';

function formatTokens(count: number | null | undefined): string {
  if (!count) return '0';
  return count.toLocaleString();
}

function formatUsd(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '$—';
  return `$${amount.toFixed(6)}`;
}

// Thoughts are a separately billed pool only when they carry a price (Gemini). Claude and OpenAI
// already fold thoughts into output_tokens, so adding them unconditionally would double-count.
// Mirrors the backend's billed_total_tokens and the desktop debugger.
function outputTokens(cost: CostBreakdown): number {
  return (
    cost.output_tokens + (cost.thought_cost_usd > 0 ? cost.thought_tokens : 0)
  );
}

const TOKEN_COLUMNS = [
  {
    label: 'W',
    title: 'Cache write tokens',
    className: 'text-amber-600',
    value: (cost: CostBreakdown) => cost.cache_write_tokens,
  },
  {
    label: 'R',
    title: 'Cache read tokens',
    className: 'text-purple-600',
    value: (cost: CostBreakdown) => cost.cached_read_tokens,
  },
  {
    label: 'I',
    title: 'Fresh input tokens',
    className: 'text-blue-600',
    value: (cost: CostBreakdown) => cost.fresh_input_tokens,
  },
  {
    label: 'O',
    title: 'Output tokens',
    className: 'text-green-600',
    value: outputTokens,
  },
];

// `cache_miss_reason` is not always a miss. The backend stores Anthropic's diagnostics verbatim
// (see _emit_cache_diagnostics in providers/call_claude.py), and these values mean the opposite of
// or nothing about a break: `no_divergence` is a cache hit, `first_turn` had nothing to compare
// against, `pending`/`unavailable` are inconclusive. Only a reported miss type is worth flagging.
const NON_BREAK_CACHE_REASONS = new Set([
  'no_divergence',
  'first_turn',
  'pending',
  'unavailable',
]);

function cacheBreakReason(payload: DebugPayloadSkeleton): string | null {
  const reason = payload.cache_miss_reason;
  if (!reason || NON_BREAK_CACHE_REASONS.has(reason)) return null;
  return reason;
}

// Same glyphs as the HTML payload debugger, so the two surfaces read alike
const SECTION_ICONS: Record<string, string> = {
  system_prompt_static: '⚙️',
  system_prompt_dynamic: '⚙️',
  tool_definition: '🔧',
  user_message: '👤',
  assistant_message: '🤖',
  thoughts: '💭',
  tool_call: '📤',
  tool_result: '📥',
  file: '📄',
  image: '🖼️',
};

/**
 * Index of the first section that differs from the previous request, or -1 when the whole shared
 * prefix matches. `content_hash` is the fingerprint; `character_count` is the fallback for older
 * sections stored before the hash existed. Everything from this index onwards had to be re-sent,
 * because a prompt cache only matches an unbroken prefix.
 */
function findCacheBreakIndex(
  sections: PayloadSection[],
  previousSections: PayloadSection[]
): number {
  for (let i = 0; i < Math.min(sections.length, previousSections.length); i++) {
    const current = sections[i];
    const previous = previousSections[i];
    const matches =
      current.content_hash && previous.content_hash
        ? current.content_hash === previous.content_hash
        : current.character_count === previous.character_count;
    if (!matches) return i;
  }
  return -1;
}

interface SectionsTableProps {
  sections: PayloadSection[];
  previousSections: PayloadSection[];
  cacheBreakReasonLabel: string | null;
}

function SectionsTable({
  sections,
  previousSections,
  cacheBreakReasonLabel,
}: SectionsTableProps) {
  if (!sections.length) {
    return <span className="text-muted-foreground">No sections stored.</span>;
  }

  const firstBreakIndex = findCacheBreakIndex(sections, previousSections);

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border/50 text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="w-8 py-1 pr-2 text-right font-medium">#</th>
          <th className="py-1 pr-3 text-left font-medium">Section</th>
          <th className="w-20 py-1 pl-3 text-right font-medium">Tokens</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((section, row) => {
          // Appended past the end of the previous request, so it was never cached
          const isNew = row >= previousSections.length;
          const isFresh =
            isNew || (firstBreakIndex >= 0 && row >= firstBreakIndex);
          return (
            <Fragment key={section.index}>
              {row === firstBreakIndex && (
                <tr>
                  <td colSpan={3} className="py-px">
                    <div className="flex items-center gap-1">
                      <div className="h-px flex-1 bg-red-400/40" />
                      <span className="whitespace-nowrap text-[9px] leading-none text-red-400">
                        cache broke here
                        {cacheBreakReasonLabel
                          ? ` · ${cacheBreakReasonLabel}`
                          : ''}{' '}
                        ↓
                      </span>
                      <div className="h-px flex-1 bg-red-400/40" />
                    </div>
                  </td>
                </tr>
              )}
              <tr
                className={cn(
                  'transition-colors hover:bg-muted/50',
                  isFresh ? 'bg-emerald-500/5' : row % 2 === 1 && 'bg-muted/25'
                )}
              >
                <td className="py-1 pr-2 text-right font-mono tabular-nums text-muted-foreground/60">
                  {row + 1}
                </td>
                <td className="py-1 pr-3 text-foreground/90">
                  <span className="mr-1.5 inline-block w-4 text-center">
                    {SECTION_ICONS[section.section_type] ?? '·'}
                  </span>
                  {section.label}
                </td>
                <td className="py-1 pl-3 text-right font-mono tabular-nums text-muted-foreground">
                  {formatTokens(section.token_count)}
                </td>
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

interface PayloadStepsTableProps {
  payloads: DebugPayloadSkeleton[];
  /** Last request of the previous turn, so step 0 has something to diff against. */
  previousTurnSections: PayloadSection[];
  onCopyPayload: (messageId: string, stepIndex: number) => Promise<void>;
}

/**
 * The requests this turn sent, one row per step, with the token split and cost the step was
 * billed. Rows expand to the stored section skeleton; the full request text is fetched only
 * when Copy payload is clicked.
 */
export default function PayloadStepsTable({
  payloads,
  previousTurnSections,
  onCopyPayload,
}: PayloadStepsTableProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [copiedStep, setCopiedStep] = useState<number | null>(null);

  const toggle = (stepIndex: number) =>
    setExpandedSteps((current) => {
      const next = new Set(current);
      if (!next.delete(stepIndex)) next.add(stepIndex);
      return next;
    });

  const copy = (messageId: string, stepIndex: number) => {
    onCopyPayload(messageId, stepIndex).then(
      () => {
        setCopiedStep(stepIndex);
        setTimeout(
          () =>
            setCopiedStep((current) =>
              current === stepIndex ? null : current
            ),
          2000
        );
      },
      // The parent surfaces the failure via toast; the button just stays as-is
      () => undefined
    );
  };

  return (
    <div className="overflow-x-auto rounded-md border border-border/60 bg-muted/20">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border/60 text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pl-2 pr-3 text-left font-medium">Step</th>
            {TOKEN_COLUMNS.map((column) => (
              <th
                key={column.label}
                title={column.title}
                className={cn(
                  'px-3 py-1.5 text-right font-medium',
                  column.className
                )}
              >
                {column.label}
              </th>
            ))}
            <th className="px-3 py-1.5 text-right font-medium">Cost</th>
            <th className="py-1.5 pl-3 pr-2" />
          </tr>
        </thead>
        <tbody>
          {payloads.map((payload, index) => {
            // Legacy rows can be stored without a step index; fall back to position so every
            // step keeps a distinct label, React key and export URL.
            const stepIndex = payload.step_index ?? index;
            const isExpanded = expandedSteps.has(stepIndex);
            const cost = payload.cost;
            const cacheBreak = cacheBreakReason(payload);
            return (
              <Fragment key={stepIndex}>
                <tr
                  onClick={() => toggle(stepIndex)}
                  className={cn(
                    'cursor-pointer border-t border-border/40 transition-colors hover:bg-muted/50',
                    index % 2 === 1 && !isExpanded && 'bg-muted/25',
                    isExpanded && 'bg-muted/40'
                  )}
                >
                  <td className="py-1.5 pl-2 pr-3">
                    <span className="flex items-center gap-1 font-medium text-foreground">
                      <ChevronRight
                        className={cn(
                          'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                          isExpanded && 'rotate-90'
                        )}
                      />
                      Step {stepIndex}
                      {cacheBreak && (
                        <span
                          title={`The cache prefix broke on this step: ${cacheBreak}`}
                          className="ml-1 whitespace-nowrap rounded border border-red-500/30 bg-red-500/10 px-1 py-px text-[9px] font-medium text-red-500"
                        >
                          cache break · {cacheBreak}
                        </span>
                      )}
                    </span>
                  </td>
                  {cost ? (
                    TOKEN_COLUMNS.map((column) => (
                      <td
                        key={column.label}
                        className={cn(
                          'px-3 py-1.5 text-right font-mono tabular-nums',
                          column.className
                        )}
                      >
                        {formatTokens(column.value(cost))}
                      </td>
                    ))
                  ) : (
                    <td
                      colSpan={TOKEN_COLUMNS.length}
                      className="px-3 py-1.5 text-right text-muted-foreground"
                    >
                      no usage recorded
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums">
                    {formatUsd(cost?.total_usd)}
                  </td>
                  <td className="py-1.5 pl-3 pr-2 text-right">
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        copy(payload.message_id, stepIndex);
                      }}
                      className={cn(
                        'whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                        copiedStep === stepIndex
                          ? 'border-green-600/40 bg-green-600/10 text-green-600'
                          : 'border-border bg-background/70 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {copiedStep === stepIndex ? 'Copied!' : 'Copy payload'}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-t border-border/40 bg-muted/10">
                    <td
                      colSpan={TOKEN_COLUMNS.length + 3}
                      className="px-2 pb-2 pt-1"
                    >
                      <SectionsTable
                        sections={payload.sections}
                        previousSections={
                          index === 0
                            ? previousTurnSections
                            : payloads[index - 1].sections
                        }
                        cacheBreakReasonLabel={cacheBreak}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
